import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from '@/lib/auth';
import { KnoxConfigProvider } from '@/lib/knox-config';
import { RequireAuth } from '@/components/RequireAuth';
import { LoginPage } from '@/pages/login/LoginPage';
import { ForgotPasswordPage } from '@/pages/forgot-password/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/reset-password/ResetPasswordPage';
import { LoginCallbackPage } from '@/pages/login-callback/LoginCallbackPage';
import { OrgBindingPage } from '@/pages/org-binding/OrgBindingPage';
import { InviteCodeEntryPage } from '@/pages/join-code/JoinCodePage';
import { ConnectorsOAuthCallbackPage } from '@/pages/connectors-oauth-callback/ConnectorsOAuthCallbackPage';

const WorkspacePage = lazy(() => import('@/routes/WorkspacePage').then(m => ({ default: m.WorkspacePage })));
const HelpCenterLayout = lazy(() => import('@/routes/HelpCenterLayout').then(m => ({ default: m.HelpCenterLayout })));
const HelpDocPage = lazy(() => import('@/routes/HelpDocPage').then(m => ({ default: m.HelpDocPage })));
const HelpFaqPage = lazy(() => import('@/routes/HelpFaqPage').then(m => ({ default: m.HelpFaqPage })));
const HelpSearchPage = lazy(() => import('@/routes/HelpSearchPage').then(m => ({ default: m.HelpSearchPage })));
const LegalPage = lazy(() => import('@/routes/LegalPage').then(m => ({ default: m.LegalPage })));

function RegisterRedirect() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  if (token) {
    localStorage.setItem('pending_invite_token', token);
  }
  return <Navigate to="/login" replace />;
}

function InviteTokenRedirect() {
  const { token } = useParams<{ token: string }>();
  if (token) {
    localStorage.setItem('pending_invite_token', token);
  }
  return <Navigate to="/login" replace />;
}

function JoinTokenRedirect() {
  const { token } = useParams<{ token: string }>();
  if (token) {
    localStorage.setItem('pending_join_token', token);
  }
  return <Navigate to="/login" replace />;
}

/** Placeholder for routes not yet fully integrated */
function RoutePlaceholder({ title }: { title?: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const feature = location.pathname.split('/')[2] || 'feature';

  return (
    <div className="flex h-full items-center justify-center bg-surface">
      <div className="text-center max-w-sm">
        <span className="material-symbols-outlined text-4xl text-on-surface-variant mb-4 block">construction</span>
        <h2 className="text-lg font-headline font-bold text-on-surface mb-2">
          {title || feature.charAt(0).toUpperCase() + feature.slice(1)}
        </h2>
        <p className="text-sm text-on-surface-variant mb-6">
          This feature is coming soon.
        </p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-primary text-on-primary-container rounded-lg text-sm font-headline font-bold"
        >
          Go Back
        </button>
      </div>
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <KnoxConfigProvider>
          <Toaster
            position="top-right"
            visibleToasts={3}
            closeButton
            className="knox-toaster"
            toastOptions={{
              className: 'font-body text-sm',
              style: {
                background: 'var(--surface-container)',
                color: 'var(--on-surface)',
                border: '1px solid var(--outline-variant)',
              },
            }}
          />
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/login/callback" element={<LoginCallbackPage />} />
            <Route path="/register" element={<RegisterRedirect />} />
            <Route path="/invite/:token" element={<InviteTokenRedirect />} />
            <Route path="/join/:token" element={<JoinTokenRedirect />} />
            <Route path="/terms" element={<Suspense><LegalPage type="terms" /></Suspense>} />
            <Route path="/privacy" element={<Suspense><LegalPage type="privacy" /></Suspense>} />
            {/* Protected routes — require login */}
            <Route element={<RequireAuth />}>
              <Route path="/workspaces/:id" element={<Suspense><WorkspacePage /></Suspense>} />
              <Route path="/org-binding" element={<OrgBindingPage />} />
              <Route path="/join" element={<InviteCodeEntryPage codeType="join" />} />
              <Route path="/invite" element={<InviteCodeEntryPage codeType="invite" />} />
              <Route path="/connectors/oauth/callback" element={<ConnectorsOAuthCallbackPage />} />
              {/* Route placeholders */}
              <Route path="/home/search" element={<RoutePlaceholder title="Message Search" />} />
              <Route path="/room-settings/*" element={<RoutePlaceholder title="Room Settings" />} />
              <Route path="/space-settings/*" element={<RoutePlaceholder title="Space Settings" />} />
              {/* Help Center */}
              <Route path="/help" element={<Suspense fallback={null}><HelpCenterLayout /></Suspense>}>
                <Route index element={<Navigate to="/help/overview" replace />} />
                <Route path="search" element={<Suspense fallback={null}><HelpSearchPage /></Suspense>} />
                <Route path=":slug" element={<Suspense fallback={null}><HelpDocPage /></Suspense>} />
                <Route path="faq" element={<Suspense fallback={null}><HelpFaqPage /></Suspense>} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </KnoxConfigProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
